import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import SyncIcon from '@mui/icons-material/Sync';
import BugCard from '../components/shapeup/BugCard';
import BugDialog from '../components/shapeup/BugDialog';
import DeclareEmergencyDialog from '../components/shapeup/DeclareEmergencyDialog';
import { BUCKETS, BUCKET_BY_VALUE } from '../components/shapeup/bugDecisions';
import { candidateFromOrgMember } from '../components/shapeup/memberRoles';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { getOrganizationMembers } from '../utils/organizationsApi';
import { getCycles, getCycleHill } from '../utils/shapeUpApi';
import {
  declareEmergency,
  deleteBug,
  escalateBug,
  fixBug,
  getBugs,
  reportBug,
  reportBugAgain,
  sweepBugs,
  syncBugs,
  updateBug,
} from '../utils/bugsApi';

/**
 * The bug pool.
 *
 * Arranged around one idea: a bug here has a decision, not a priority. So the tabs
 * are the decisions, ordered by what needs attention rather than by severity, and
 * Untriaged leads because it is the only list asking the team for anything. There
 * is no sort control and no ranking, because a rankable list of bugs is a queue,
 * and keeping a queue honest is the grooming work this is built to avoid.
 */
const BugsPage = () => {
  const { organizations, currentOrganization } = useWorkspace();
  const { user } = useAuth();

  const [organizationId, setOrganizationId] = useState('');
  const [bugs, setBugs] = useState([]);
  const [counts, setCounts] = useState({});
  const [settings, setSettings] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState(null);

  const [tab, setTab] = useState('untriaged');
  const [mineOnly, setMineOnly] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [liveBets, setLiveBets] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emergencyFor, setEmergencyFor] = useState(null);
  const [emergencyCount, setEmergencyCount] = useState(0);

  const canEdit = userRole && userRole !== 'viewer';
  const canDelete = ['admin', 'owner'].includes(userRole);

  useEffect(() => {
    if (currentOrganization?.id) {
      setOrganizationId(currentOrganization.id);
    } else if (organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    } else {
      setLoading(false);
    }
  }, [currentOrganization, organizations]);

  const load = useCallback(async (orgId) => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBugs(orgId);
      setBugs(data.bugs || []);
      setCounts(data.counts || {});
      setSettings(data.settings || {});
      setUserRole(data.userRole || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the bug pool.');
      setBugs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (organizationId) load(organizationId); }, [organizationId, load]);

  useEffect(() => {
    if (!organizationId) return;
    getOrganizationMembers(organizationId)
      .then(members => setCandidates((members || []).map(candidateFromOrgMember)))
      .catch(() => setCandidates([]));
  }, [organizationId]);

  // The bets an emergency could be charged against: only those in a cycle that is
  // actually running, since interrupting a finished cycle is not a thing that can
  // happen and offering one would produce a nonsense record.
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { cycles = [] } = await getCycles({ organizationId });
        const live = cycles.filter(c => ['active', 'cooldown'].includes(c.status) && !c.archivedAt);
        const hills = await Promise.all(live.map(c => getCycleHill(c.id).catch(() => null)));
        if (cancelled) return;
        setLiveBets(hills.flatMap((hill, i) => (hill?.bets || []).map(bet => ({
          id: bet.id,
          name: bet.name,
          cycleName: live[i].name,
        }))));
      } catch {
        if (!cancelled) setLiveBets([]);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  // Land on the first bucket that has something in it, preferring untriaged, so
  // opening the page does not present an empty tab when work is waiting elsewhere.
  useEffect(() => {
    if (loading || counts.untriaged === undefined) return;
    if (counts[tab]) return;
    const firstWithContent = BUCKETS.find(bucket => counts[bucket.value] > 0);
    if (firstWithContent) setTab(firstWithContent.value);
  // Only on a fresh load: re-running on every count change would yank the user off
  // a tab the moment they emptied it, which is exactly when they want to see it empty.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, organizationId]);

  const visible = useMemo(() => {
    const inBucket = bugs.filter(bug => bug.bucket === tab);
    return mineOnly
      ? inBucket.filter(bug => bug.assignee?.userId === user?.id)
      : inBucket;
  }, [bugs, tab, mineOnly, user]);

  const shown = (result, fallback) => {
    setToast(result?.message || fallback);
  };

  /** Every mutation reloads: bucket, age, and fading are all computed server-side. */
  const run = async (bug, fn, fallbackMessage) => {
    setBusyId(bug?.id || null);
    try {
      const result = await fn();
      await load(organizationId);
      shown(result, fallbackMessage);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'That did not work.');
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const handleTriage = (bug, disposition, note) => run(
    bug,
    () => updateBug(bug.id, organizationId, { disposition, ...(note ? { dispositionNote: note } : {}) }),
    'Decision recorded.'
  );

  const handleClaim = (bug) => run(
    bug,
    () => updateBug(bug.id, organizationId, { assigneeUserId: user.id }),
    'Yours now.'
  );

  const handleFix = (bug) => run(bug, async () => {
    // Asked for only on an emergency, and only at this point, because the cost of
    // an interruption is not knowable when it is declared.
    let costDays;
    if (bug.disposition === 'emergency' && !bug.emergencyCostDays) {
      const answer = window.prompt(
        `Roughly how many days did this cost ${bug.interruptedBet?.name || 'the bet it interrupted'}?`,
        '1'
      );
      if (answer !== null && answer.trim() !== '') costDays = Number(answer);
    }
    const result = await fixBug(bug.id, organizationId, Number.isFinite(costDays) ? costDays : undefined);
    return {
      message: result.creditedTo
        ? `Fixed, credited to ${result.creditedTo.name}.`
        : 'Fixed.',
    };
  }, 'Fixed.');

  const handleEscalate = (bug) => run(
    bug,
    async () => {
      const result = await escalateBug(bug.id, organizationId);
      return { message: `Shaped into the pitch “${result.pitch.title}”. It goes to the betting table now.` };
    },
    'Shaped into a pitch.'
  );

  const handleReportAgain = (bug) => run(bug, () => reportBugAgain(bug.id, organizationId), 'Noted.');

  const handleDelete = (bug) => {
    if (!window.confirm(`Delete “${bug.title}” for good? Deciding against it keeps the record instead.`)) return;
    run(bug, () => deleteBug(bug.id, organizationId), 'Deleted.');
  };

  const openEmergency = async (bug) => {
    // The running count comes from bugs already charged against this cycle's bets,
    // so the dialog can say "this would be the fourth" before anyone commits.
    setEmergencyCount(bugs.filter(b => b.disposition === 'emergency' && b.status === 'open').length);
    setEmergencyFor(bug);
  };

  const submitEmergency = async ({ interruptedBetId, reason }) => {
    setWorking(true);
    try {
      const result = await declareEmergency(emergencyFor.id, organizationId, { interruptedBetId, reason });
      setEmergencyFor(null);
      await load(organizationId);
      setToast(
        result.overBudget
          ? `Declared. That is ${result.emergencyCount} emergencies in ${result.cycle.name}, against a budget of ${result.budget}.`
          : `Declared, and charged against ${result.bug.interruptedBet?.name}.`
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Could not declare that.');
    } finally {
      setWorking(false);
    }
  };

  const submitBug = async (payload) => {
    setWorking(true);
    try {
      if (editing) {
        await updateBug(editing.id, organizationId, payload);
        setToast('Saved.');
      } else {
        const result = await reportBug(organizationId, payload);
        setToast(result.jira?.ok ? result.jira.message : 'Reported. It is untriaged until someone decides.');
        if (result.jira && !result.jira.ok) setError(result.jira.error);
      }
      setDialogOpen(false);
      setEditing(null);
      await load(organizationId);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save that.');
    } finally {
      setWorking(false);
    }
  };

  const handleSweep = async () => {
    const fading = counts.fading || 0;
    if (!fading) return;
    if (!window.confirm(
      `Archive ${fading} faded ${fading === 1 ? 'bug' : 'bugs'}? Nothing is lost — anything real comes back when it is reported again.`
    )) return;
    setWorking(true);
    try {
      const result = await sweepBugs(organizationId);
      await load(organizationId);
      setToast(result.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sweep.');
    } finally {
      setWorking(false);
    }
  };

  const handleSync = async () => {
    setWorking(true);
    try {
      const result = await syncBugs(organizationId);
      await load(organizationId);
      setToast(result.synced
        ? `Pulled ${result.total} from Jira: ${result.created} new, ${result.closed} closed there.`
        : result.reason);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sync.');
    } finally {
      setWorking(false);
    }
  };

  const bucketHint = BUCKET_BY_VALUE[tab]?.hint;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/shape-up" underline="hover" color="inherit">
          Cycles
        </Link>
        <Typography color="text.primary">Bugs</Typography>
      </Breadcrumbs>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon color="action" /> Bugs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A pool, not a backlog. Each of these gets a decision rather than a
            priority, and anything nobody picks up fades — if it matters, it comes back.
          </Typography>
        </Box>

        {organizations.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Workspace</InputLabel>
            <Select
              value={organizationId}
              label="Workspace"
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              {organizations.map(org => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
          >
            Report a bug
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <Button
          size="small"
          variant={mineOnly ? 'contained' : 'outlined'}
          onClick={() => setMineOnly(m => !m)}
        >
          {mineOnly ? 'Showing only mine' : 'Only mine'}
        </Button>

        {canEdit && settings.jiraConnected && (
          <Button size="small" startIcon={<SyncIcon />} disabled={working} onClick={handleSync}>
            Pull from Jira
          </Button>
        )}

        {canEdit && (counts.fading || 0) > 0 && (
          <Tooltip title="Archives everything nobody picked up. Reversible: a bug reported again comes straight back.">
            <Button
              size="small"
              startIcon={<CleaningServicesIcon />}
              disabled={working}
              onClick={handleSweep}
            >
              Sweep {counts.fading} faded
            </Button>
          </Tooltip>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {BUCKETS.map(bucket => (
            <Tab
              key={bucket.value}
              value={bucket.value}
              label={(
                <Badge
                  badgeContent={counts[bucket.value] || 0}
                  color={bucket.value === 'untriaged' ? 'primary' : 'default'}
                  sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}
                >
                  <Box sx={{ pr: 1.5 }}>{bucket.label}</Box>
                </Badge>
              )}
            />
          ))}
        </Tabs>
      </Paper>

      {bucketHint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {bucketHint}
        </Typography>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={110} />)}
        </Stack>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {tab === 'untriaged'
              ? 'Nothing waiting on a decision.'
              : `Nothing in ${(BUCKET_BY_VALUE[tab]?.label || tab).toLowerCase()}.`}
          </Typography>
          {tab === 'untriaged' && (counts.cooldown || 0) > 0 && (
            <Typography variant="caption" color="text.secondary">
              {counts.cooldown} queued for the next cool-down.
            </Typography>
          )}
        </Paper>
      ) : (
        <Stack spacing={2}>
          {visible.map(bug => (
            <BugCard
              key={bug.id}
              bug={bug}
              canEdit={canEdit}
              currentUserId={user?.id}
              busy={busyId === bug.id}
              onTriage={handleTriage}
              onEmergency={openEmergency}
              onFix={handleFix}
              onEscalate={handleEscalate}
              onClaim={handleClaim}
              onEdit={(b) => { setEditing(b); setDialogOpen(true); }}
              onReportAgain={handleReportAgain}
              onDelete={canDelete ? handleDelete : null}
            />
          ))}
        </Stack>
      )}

      <BugDialog
        open={dialogOpen}
        bug={editing}
        candidates={candidates}
        jiraConnected={Boolean(settings.jiraConnected)}
        saving={working}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSubmit={submitBug}
      />

      <DeclareEmergencyDialog
        open={Boolean(emergencyFor)}
        bug={emergencyFor}
        bets={liveBets}
        emergencyCount={emergencyCount}
        budget={settings.emergencyBudgetPerCycle ?? 2}
        saving={working}
        onClose={() => setEmergencyFor(null)}
        onSubmit={submitEmergency}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default BugsPage;
